<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';
  import { Loader2 } from 'lucide-vue-next';
  import { ColoredButton } from '@/shared/ui';
  import { useTrainingMatrixEditBatch } from '../model/useTrainingMatrixEditBatch';
  import { parsePositionIdsQuery } from '../lib/parsePositionIdsQuery';
  import TrainingMatrixDocumentSection from './TrainingMatrixDocumentSection.vue';

  const route = useRoute();
  const positionIds = computed(() => parsePositionIdsQuery(route.query.positionIds));

  const {
    sections,
    positions,
    loading,
    isSaving,
    updateSearch,
    updateSearchField,
    updateSelectedItems,
    save,
    cancel,
  } = useTrainingMatrixEditBatch(positionIds.value);
</script>

<template>
  <div
    data-testid="training-matrix-edit-batch-page"
    class="p-6"
  >
    <div
      v-if="loading"
      class="flex justify-center py-12"
      data-testid="training-matrix-edit-batch-loading"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <template v-else>
      <h1
        data-testid="training-matrix-edit-batch-title"
        class="text-xl font-semibold text-gray-07 mb-6"
      >
        Editar Matriz
      </h1>

      <div class="flex flex-col gap-5 rounded-t-2xl border border-b-0 border-gray-02 bg-white-01">
        <div class="border-b border-gray-02 px-6 py-3 flex gap-2 text-sm">
          <h3 class="font-semibold text-gray-07 shrink-0">Cargos:</h3>
          <div
            data-testid="training-matrix-edit-batch-positions"
            class="flex flex-col gap-2 max-h-40 overflow-y-auto"
          >
            <p
              v-for="position in positions"
              :key="position.id"
              class="font-medium"
            >
              {{ position.name }}
            </p>
          </div>
        </div>

        <TrainingMatrixDocumentSection
          v-for="(section, index) in sections"
          :key="section.prefixCode"
          :section="section"
          :section-index="index"
          @search="updateSearch"
          @update:search-field="updateSearchField"
          @update:selected-items="updateSelectedItems"
        />
      </div>
    </template>

    <div class="sticky bottom-0 z-10 flex justify-end gap-6 rounded-b-2xl border border-t-0 border-gray-02 bg-white-01 px-6 py-8">
      <ColoredButton
        text="Cancelar"
        outlined
        :disabled="isSaving"
        @click="cancel"
      />
      <ColoredButton
        text="Salvar"
        :loading="isSaving"
        @click="save"
      />
    </div>
  </div>
</template>
